import { z } from "zod";

/**
 * Shared DTO cho Admin AI cost-guard & rate-limit (US-AE8.F1-0026):
 *  - @UC9070 ai-cost-guard  — GET/PATCH /admin/ai/budgets (trần token + ngưỡng alert + rate-limit +
 *    hành-vi-vượt-trần đọc/ghi từ system_settings) · GET /admin/ai/usage (token/tháng per nhóm).
 *  - @UC9071 ai-rate-limit  — cấu hình rate-limit message/phút/user (nằm trong budgets config).
 *
 * Một nguồn sự thật shape cho api (S01) + admin web (S03) — tránh drift DTO ở 2 nơi (DRY).
 *
 * BỐI CẢNH: App-Lane AI chat (E11) DEFER → guard là HẠ TẦNG oversight standalone. DTO mô tả
 * config trần/rate-limit + bảng metering `ai_usage`; KHÔNG có field của AI chat.
 *
 * BẢO MẬT / BẤT BIẾN (invariants):
 * - Token = SỐ NGUYÊN (không phải tiền → number int, KHÔNG float). `budgetTokens`/`tokensUsed` int.
 * - Tiền (`costMinor`) = BigInt minor units → serialize STRING trên wire + redact theo vai
 *   (SUPPORT/READ_ONLY → null) — mirror redaction matrix admin (KHÔNG number/float).
 * - `.strict()` — KHÔNG khai báo field lạ; reject tại biên (chống leak).
 * - PATCH validate theo `allowed` guardrail của setting (min/max) — refine reject vượt phạm vi.
 * - Cursor opaque string — đối xứng common/pagination/cursor.
 * - G2: DTO chỉ mô tả ĐỌC metering (ai_usage) + config; KHÔNG field mutating ledger.
 */

/** Max page-size cho bảng usage. Default (20) do backend áp khi vắng. */
export const ADMIN_AI_USAGE_MAX_LIMIT = 100;

/**
 * Guardrail v1 cho trần token (khớp seed system_settings `ai_token_budget_monthly.allowed`).
 *
 * 🔴 US-AI.F4-0233 (@UC9400 @BUDGET-ZERO-FAILOPEN): MIN hạ xuống **-1** = `AI_BUDGET_UNLIMITED` (khai
 * TƯỜNG MINH "không giới hạn"). Giá trị **0 KHÔNG còn là 'tắt guard'** — nó nghĩa là "chưa cấu hình
 * ngân sách" ⇒ engine **CHẶN** (fail-closed). Ai muốn vô hạn phải cố ý gõ -1 và biết mình đang làm gì.
 */
export const AI_TOKEN_BUDGET_MIN = -1;
export const AI_TOKEN_BUDGET_MAX = 100_000_000;
/** Guardrail v1 cho rate-limit msg/phút (khớp `ai_rate_limit_messages_per_min.allowed`). */
export const AI_RATE_LIMIT_MIN = 1;
export const AI_RATE_LIMIT_MAX = 120;
/** Guardrail v1 cho ngưỡng alert % (khớp `ai_token_alert_threshold_pct.allowed`). */
export const AI_ALERT_PCT_MIN = 0;
export const AI_ALERT_PCT_MAX = 100;

/** Hành vi khi usage vượt trần token: chặn (block) hoặc xếp hàng chờ kỳ sau (queue). */
export const aiCapActionSchema = z.enum(["block", "queue"]);
export type AiCapAction = z.infer<typeof aiCapActionSchema>;

/**
 * GET /admin/ai/budgets — config cost-guard + rate-limit hiện hành (đọc từ system_settings).
 *  - `tokenBudgetMonthly`: trần token/nhóm/tháng (số nguyên; 0 = tắt guard).
 *  - `alertThresholdPct`: ngưỡng % usage/trần để cảnh báo 'near' (0..100).
 *  - `rateLimitMessagesPerMin`: số message AI/phút/user (rate-limit Redis).
 *  - `capAction`: hành vi khi vượt trần (block|queue).
 */
export const getAiBudgetResponseSchema = z
  .object({
    tokenBudgetMonthly: z.number().int().min(AI_TOKEN_BUDGET_MIN).max(AI_TOKEN_BUDGET_MAX),
    alertThresholdPct: z.number().int().min(AI_ALERT_PCT_MIN).max(AI_ALERT_PCT_MAX),
    rateLimitMessagesPerMin: z.number().int().min(AI_RATE_LIMIT_MIN).max(AI_RATE_LIMIT_MAX),
    capAction: aiCapActionSchema,
  })
  .strict();
export type GetAiBudgetResponse = z.infer<typeof getAiBudgetResponseSchema>;

/**
 * PATCH /admin/ai/budgets — đổi 1 hoặc nhiều field config (partial). `.strict()` chặn key lạ;
 * `.refine` ép ≥1 field (no-op rỗng vô nghĩa). Mỗi field validate theo guardrail `allowed` tương ứng
 * (min/max/enum) — refine reject vượt phạm vi TRƯỚC khi gửi (C5 client-side; server enforce lại).
 * Token = số nguyên (no float).
 */
export const patchAiBudgetRequestSchema = z
  .object({
    tokenBudgetMonthly: z
      .number()
      .int()
      .min(AI_TOKEN_BUDGET_MIN)
      .max(AI_TOKEN_BUDGET_MAX)
      .optional(),
    alertThresholdPct: z.number().int().min(AI_ALERT_PCT_MIN).max(AI_ALERT_PCT_MAX).optional(),
    rateLimitMessagesPerMin: z
      .number()
      .int()
      .min(AI_RATE_LIMIT_MIN)
      .max(AI_RATE_LIMIT_MAX)
      .optional(),
    capAction: aiCapActionSchema.optional(),
  })
  .strict()
  .refine((b) => Object.keys(b).length > 0, {
    message: "Phải cung cấp ít nhất một field để cập nhật",
  });
export type PatchAiBudgetRequest = z.infer<typeof patchAiBudgetRequestSchema>;

/** Trạng thái usage so với trần: ok (<alert%), near (≥alert% & <100%), over (≥100%). */
export const aiUsageStatusSchema = z.enum(["ok", "near", "over"]);
export type AiUsageStatus = z.infer<typeof aiUsageStatusSchema>;

/**
 * Một dòng usage AI (per nhóm, kỳ hiện tại). Token = số nguyên. `costMinor` = BigInt minor VND →
 * STRING trên wire + redact theo vai (SUPPORT/READ_ONLY → null). `usagePct` = TỈ LỆ (số nguyên
 * 0..∞, có thể >100 khi over) — KHÔNG phải tiền. `groupName` nullable+optional (redact theo vai).
 */
export const aiUsageRowSchema = z
  .object({
    groupId: z.string(),
    /** Tên nhóm (PII nhẹ) — redact theo vai nếu chính sách yêu cầu; optional. */
    groupName: z.string().nullable().optional(),
    /** Kỳ 'YYYY-MM'. */
    period: z.string(),
    /** Token đã dùng (prompt+completion) trong kỳ — số nguyên. */
    tokensUsed: z.number().int().min(0),
    /** Trần token/tháng hiệu lực cho nhóm (số nguyên; 0 = không trần). */
    budgetTokens: z.number().int().min(0),
    /** % usage/trần (số nguyên; >100 khi over; 0 khi budget=0). */
    usagePct: z.number().int().min(0),
    /** Trạng thái so trần. */
    status: aiUsageStatusSchema,
    /** Chi phí ước tính (BigInt minor VND → string) — redact theo vai → null. */
    costMinor: z.string().nullable(),
  })
  .strict();
export type AiUsageRow = z.infer<typeof aiUsageRowSchema>;

/** Envelope list usage (cursor pagination) — đối xứng repo {data, pageInfo}. */
export const aiUsageListResponseSchema = z
  .object({
    data: z.array(aiUsageRowSchema),
    pageInfo: z
      .object({
        hasNext: z.boolean(),
        nextCursor: z.string().nullable(),
      })
      .strict(),
  })
  .strict();
export type AiUsageListResponse = z.infer<typeof aiUsageListResponseSchema>;
